export enum GoogleNotificationTypes {
  NewReview = 'NEW_REVIEW',
  UpdateReview = 'UPDATED_REVIEW',
}

export enum IntegrationSources {
  Facebook = 'Facebook',
  Instagram = 'Instagram',
  TikTok = 'TikTok',
  Google = 'Google',
  Yelp = 'Yelp',
  Adyen = 'Adyen',
  OnePayments = 'One Payments',
  Tripadvisor = 'Tripadvisor',
}

export enum SocialContentTypes {
  Image = 'Image',
  Video = 'Video',
}

export enum TikTokPrivacyLevels {
  PUBLIC_TO_EVERYONE = 'PUBLIC_TO_EVERYONE',
  FOLLOWER_OF_CREATOR = 'FOLLOWER_OF_CREATOR',
  MUTUAL_FOLLOW_FRIENDS = 'MUTUAL_FOLLOW_FRIENDS',
  SELF_ONLY = 'SELF_ONLY',
}

export enum TikTokErrorCode {
  INVALID_PARAM = 'invalid_params',
  APP_VERSION_CHECK_FAILED = 'app_version_check_failed',
  SPAM_RISK_TOO_MANY_POSTS = 'spam_risk_too_many_posts',
  SPAM_RISK_USER_BANNED_FROM_POSTING = 'spam_risk_user_banned_from_posting',
  SPAM_RISK_TOO_MANY_PENDING_SHARE = 'spam_risk_too_many_pending_share',
  REACHED_ACTIVE_USER_CAP = 'reached_active_user_cap',
  UNAUDITED_CLIENT_POST_TO_PRIVATE_ONLY = 'unaudited_client_can_only_post_to_private_accounts',
  URL_OWNERSHIP_UNVERIFIED = 'url_ownership_unverified',
  PRIVACY_LEVEL_OPTION_MISMATCH = 'privacy_level_option_mismatch',
  ACCESS_TOKEN_INVALID = 'access_token_invalid',
  SCOPE_NOT_AUTHORIZED = 'scope_not_authorized',
  RATE_LIMIT_EXCEEDED = 'rate_limit_exceeded',
  INTERNAL_ERROR = 'internal_error',
  TIKTOK_SERVER_ERROR = 'tiktok_server_error', // New: TikTok server or network error
}

export enum TikTokFailReasonCode {
  FILE_FORMAT_CHECK_FAILED = 'file_format_check_failed',
  DURATION_CHECK_FAILED = 'duration_check_failed',
  FRAME_RATE_CHECK_FAILED = 'frame_rate_check_failed',
  PICTURE_SIZE_CHECK_FAILED = 'picture_size_check_failed',
  INTERNAL = 'internal',
  VIDEO_PULL_FAILED = 'video_pull_failed',
  PHOTO_PULL_FAILED = 'photo_pull_failed',
  PUBLISH_CANCELLED = 'publish_cancelled',
  AUTH_REMOVED = 'auth_removed',
  SPAM_RISK_TOO_MANY_POSTS = 'spam_risk_too_many_posts',
  SPAM_RISK_USER_BANNED_FROM_POSTING = 'spam_risk_user_banned_from_posting',
  SPAM_RISK_TEXT = 'spam_risk_text',
  SPAM_RISK = 'spam_risk',
}

export enum MetaPhotoErrorCode {
  ACTION_DEEMED_ABUSIVE = 368,
  PERMISSIONS_ERROR = 200,
  INVALID_OAUTH_TOKEN = 190,
  INVALID_PARAMETER = 100,
  MISSING_OR_INVALID_IMAGE_FILE = 324,
  TOO_MANY_CALLS_TO_PAGE_ACCOUNT = 80001,
  DESKTOP_APP_FUNCTION_RESTRICTED = 240,
  INVALID_VAULT_IMAGE_ID = 122,
}

export enum YelpSubscriptionTypes {
  WEBHOOK = 'WEBHOOK',
  YELP_KNOWLEDGE = 'YELP_KNOWLEDGE',
}

export enum YelpReviewEventType {
  NEW_REVIEW = 'NEW_REVIEW_EVENT',
  REVIEW_EDITED = 'REVIEW_EDITED_EVENT',
  REVIEW_REMOVED = 'REVIEW_REMOVED_EVENT', // include if Yelp supports it
}

interface GPSCoordinates {
  latitude: number;
  longitude: number;
}

interface OperatingHours {
  thursday: string;
  friday: string;
  saturday: string;
  sunday: string;
  monday: string;
  tuesday: string;
  wednesday: string;
}

interface ServiceOptions {
  dine_in: boolean;
  takeout: boolean;
  no_contact_delivery: boolean;
}

export interface IIntegrationDetails {
  [IntegrationSources.Facebook]?: IMetaAccount[];
  [IntegrationSources.Instagram]?: IInstagramAccount[];
  [IntegrationSources.Google]?: IGoogleAccount;
  [IntegrationSources.TikTok]?: ITikTokCreator;
  [IntegrationSources.Tripadvisor]?: ITripadvisorLocationDetails;
  [IntegrationSources.Yelp]?: IYelpAccount;
}

export interface IYelpAccount {
  name: string;
  image_url: string;
}

export interface IIntegrationsResponse {
  googleIntegration: boolean;
  fbIntegration: boolean;
  instaIntegration: boolean;
  tiktokIntegration: boolean;
  tripadvisorIntegration: boolean;
  yelpIntegration: boolean;
  details: IIntegrationDetails;
}

export interface IGoogleAccount {
  name: string;
  title: string;
  createdAt?: string;
  picture?: string;
  email?: string;
  location?: { title: string };
}

export interface IInstagramAccount {
  name: string;
  username: string;
  profile_picture_url?: string;
  id: string;
}

export interface IMetaAccount {
  id: string;
  name: string;
  picture?: {
    data?: {
      url?: string;
    };
  };
  access_token: string;
  instagram_business_account?: IInstagramAccount;
}

export interface ITripadvisorLocation {
  location_id: string;
  name: string;
  address_obj: {
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    country: string;
    postalcode?: string;
    address_string: string;
  };
}

export interface ITripadvisorReview {
  id: number;
  lang: string;
  location_id: number;
  published_date: string;
  rating: number;
  helpful_votes: number;
  rating_image_url: string;
  url: string;
  text: string;
  title: string;
  trip_type: string;
  travel_date: string;
  user: {
    username: string;
    user_location: {
      id: string | null;
      name?: string;
    };
    avatar: {
      thumbnail: string;
      small: string;
      medium: string;
      large: string;
      original: string;
    };
  };
  subratings: {
    [key: string]: {
      name: string;
      rating_image_url: string;
      value: number;
      localized_name: string;
    };
  };
}

export interface ITripadvisorLocationDetails {
  location_id: number;
  name: string;
  description: string;
  web_url: string;
  address_obj: {
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    country?: string;
    postalcode?: string;
  };
  address_string: string;
  ancestors: {
    abbrv?: string;
    level: string;
    name: string;
    location_id: number;
  }[];
  latitude?: number;
  longitude?: number;
  timezone?: string;
  email?: string;
  phone?: string;
  website?: string;
  write_review?: string;
  ranking_data?: {
    geo_location_id: number;
    ranking_string: string;
    geo_location_name: string;
    ranking_out_of: number;
    ranking: number;
  };
  rating?: number;
  rating_image_url?: string;
  num_reviews: string;
  review_rating_count?: {
    1?: number;
    2?: number;
    3?: number;
    4?: number;
    5?: number;
  };
  subratings?: Record<string, any>;
  photo_count?: number;
  see_all_photos?: string;
  price_level?: string;
  hours?: {
    periods: {
      open: {
        day: number;
        time: string;
      };
      close: {
        day: number;
        time: string;
      };
    }[];
    weekday_text: string[];
  };
  amenities?: string[];
  features?: string[];
  cuisine?: {
    name: string;
    localized_name: string;
  }[];
  parent_brand?: string;
  brand?: string;
  category?: {
    name: string;
    localized_name: string;
    subcategory: {
      name: string;
      localized_name: string;
    }[];
  };
  groups?: {
    name: string;
    localized_name: string;
    categories: {
      name: string;
      localized_name: string;
    }[];
  }[];
  styles?: string[];
  neighborhood_info?: {
    location_id: string;
    name: string;
  }[];
  trip_types?: {
    name: string;
    localized_name: string;
    value: string;
  }[];
  awards?: {
    award_type: string;
    year: number;
    images: Record<string, string>; // Or { small: string; large: string; ... } if sizes are known
    categories: string[];
    display_name: string;
  }[];
}

export interface IMetaPages {
  [IntegrationSources.Facebook]: IMetaAccount[];
  [IntegrationSources.Instagram]: IInstagramAccount[];
}

export interface ITikTokCreator {
  creator_avatar_url: string;
  creator_username: string;
  creator_nickname: string;
  privacy_level_options: TikTokPrivacyLevels[];
  comment_disabled: boolean;
  duet_disabled: boolean;
  stitch_disabled: boolean;
  max_video_post_duration_sec: number;
}

export interface ISerpYelpOrganicResult {
  position: number;
  place_ids: string[];
  title: string;
  link: string;
  reviews_link: string;
  categories: {
    title: string;
    link: string;
  }[];
  price: string;
  rating: number;
  reviews: number;
  address: string;
  neighborhoods?: string;
  phone: string;
  snippet: string;
  service_options: {
    outdoor_seating?: boolean;
    delivery?: boolean;
    takeout?: boolean;
    curbside_pickup?: boolean;
  };
  thumbnail: string;
}

export interface ISerpGoogleResult {
  position: number;
  title: string;
  place_id: string;
  data_id: string;
  data_cid: string;
  reviews_link: string;
  photos_link: string;
  gps_coordinates: GPSCoordinates;
  place_id_search: string;
  provider_id: string;
  rating: number;
  reviews: number;
  price: string;
  type: string;
  types: string[];
  type_id: string;
  type_ids: string[];
  address: string;
  open_state: string;
  hours: string;
  operating_hours: OperatingHours;
  phone: string;
  website: string;
  description: string;
  service_options: ServiceOptions;
  order_online: string;
  thumbnail: string;
}

export interface IGoogleReviewResponse {
  comment: string;
  starRating: string;
  reviewer: { displayName: string };
  createTime: string;
  name: string;
  reviewReply?: { comment: string; updateTime: string };
}

export interface NotificationData {
  type: string;
  location: string;
  review: string;
  [key: string]: any;
}

export interface GooglePayload {
  message: GooglePayloadMessage;
  subscription: string;
}

export interface GooglePayloadMessage {
  data: string;
  messageId: string;
  message_id: string;
  publishTime: string;
  publish_time: string;
}

export interface IGoogleLocation {
  name: string;
  title: string;
  storefrontAddress: {
    administrativeArea?: string;
    locality: string;
  };
  disabled: boolean;
}

export interface IGoogleLocationCheckResponse {
  isLocationExists: boolean;
  isGoogleConnected: boolean;
}

export interface IGoogleLocationResponse {
  locations: IGoogleLocation[];
  username?: string;
  accountId?: string;
}

export interface IYelpBusiness {
  id: string;
  alias: string;
  name: string;
  image_url: string;
  is_claimed: boolean;
  is_closed: boolean;
  url: string;
  phone: string;
  display_phone: string;
  review_count: number;
  categories: {
    alias: string;
    title: string;
  }[];
  rating: number;
  location: {
    address1: string;
    address2: string;
    address3: string | null;
    city: string;
    zip_code: string;
    country: string;
    state: string;
    display_address: string[];
    cross_streets: string;
  };
  coordinates: {
    latitude: number;
    longitude: number;
  };
  photos: string[];
  transactions: string[];
  messaging: {
    url: string;
    use_case_text: string;
  };
  disabled: boolean;
}

export interface IYelpProfile {
  id: string;
  first_name: string;
  last_name: string;
  photo_url: string;
  position: string;
  email: string;
}

export interface IYelpBusinessCheckResponse {
  isBusinessExists: boolean;
  isYelpConnected: boolean;
  yelpBusinessId: string | null;
}

export interface IYelpReviewReply {
  text: string;
  time_created: string;
  business_user: {
    name: string;
    role: string;
    photo_url: string;
  };
}

export interface IYelpReview {
  time_created: string;
  id: string;
  rating: number;
  text: string;
  url: string;
  user: {
    name: string;
    image_url: string;
    id: string;
  };
  public_response?: IYelpReviewReply | null;
}

export interface ISerpYelpReview {
  user: {
    name: string;
    user_id: string;
    link: string;
    thumbnail?: string; // Optional since some users may not have thumbnails
    address: string;
    friends: number;
    photos: number;
    reviews: number;
    elite_year?: number; // Optional since not all users may have elite years
  };
  comment: {
    text: string;
    language: string;
  };
  date: string; // ISO string for the date
  rating: number; // Assuming integer values for rating
  tags?: string[]; // Optional since not all reviews may have tags
  photos?: {
    link: string;
  }[]; // Optional since not all reviews may have photos
  feedback: {
    useful: number;
    funny: number;
    cool: number;
  };
}

export interface ISerpGoogleReview {
  link: string;
  rating: number;
  date: string; // Example: "a day ago"
  iso_date: string; // ISO 8601 format
  iso_date_of_last_edit: string; // ISO 8601 format
  source: string; // Example: "Google"
  review_id: string;
  user: {
    name: string;
    link: string;
    contributor_id: string;
    thumbnail: string;
    local_guide?: boolean; // Optional for users who are Local Guides
    reviews: number;
    photos: number;
  };
  details?: {
    food?: number;
    service?: number;
    atmosphere?: number;
    meal_type?: string; // Example: "Dinner"
    price_per_person?: string; // Example: "$20–30"
  };
  snippet?: string; // Summary of the review
  extracted_snippet?: {
    original: string; // The original review text
  };
  likes: number; // Number of likes for the review
}
